// Package imports:
import React, { useContext, useState } from 'react';
import { format } from "date-fns";
// Component imports:
import LmdTable from '../../ui-elements/Table/LmdTable';
import SmallSearch from '../SmallSearch/SmallSearch';
import DisplayBox from '../DisplayBox/DisplayBox';
import Button from '../../ui-elements/Button/Button';
import HeadSection from '../../ui-elements/HeadSection/HeadSection';
import Tooltip from '../../ui-elements/Tooltip/Tooltip';
// Service imports:
import { useApiLmdData, useApiLmdDataMappedByString } from '../../services/apiHooks';
import { icelandifyString } from '../../services/utils';
// Context imports:
import { AccessTokenContext } from '../../contexts/AccessTokenContext';

interface IKaupsamningar {
    Einflm: number,
    Emnr: number,
    Faerslunumer: number,
    Fasteignamat: number,
    Fastnum: number,
    Fullbuid: boolean,
    Heimilisfang: string,
    Heinum: number,
    Kaupverd: number,
    LodFlm: number,
    LodFlmein: string,
    OnothaefurSamningur: boolean,
    Postnr: number,
    Skjalanumer: string,
    Sveitarfelag: string,
    Svfn: number,
    Tegund: string,
    Thinglystdags: string,
    Utgdag: string
}


function numberWithDots(num: string) {
    return num.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ".");
}

const Kaupsamningar: React.FC = () => {
    const accessToken = useContext(AccessTokenContext);

    const [ latestKaupsamningar ] = useApiLmdData<IKaupsamningar[]>(`/static_data/v1/register/realestate_sales/latest?limit=50`, accessToken);
    const [ mostRecentlyFetchedKaupsamningar, setMostRecentlyFetchedKaupsamningar ] = useState<IKaupsamningar[]>([]);
    const [ searchInput, setSearchInput, kaupsamningarForSearchInput ] = useApiLmdDataMappedByString<IKaupsamningar[]>(
        '',
        (searchInput: string) => `/v1/static_data/v1/register/realestate_sales/address/${searchInput}*/?limit=50&offset=0`,
        accessToken,
        undefined,
        undefined,
        (kaupsamningar) => setMostRecentlyFetchedKaupsamningar(kaupsamningar)
    );

    const kaupsamningar =
        (searchInput.length < 3)
        ? latestKaupsamningar
        : (kaupsamningarForSearchInput.data === null && kaupsamningarForSearchInput.error === null)
        ? ({
            data: mostRecentlyFetchedKaupsamningar,
            error: null
        })
        : kaupsamningarForSearchInput;

    
    return (
        <div className='main KCL_kaupsamningar'>
            <div className="section section--primary">
                <div className='section__body'>
                    <div className='shell'>
                        <HeadSection
                            title='Kaupsamningar'
                            description={
                                <p>
                                    Hér má finna kaupskrá fasteigna frá HMS. HMS skráir upplýsingarnar upp úr þinglýstum kaupsamningum.<br/>
                                    Kaupsamningar geta verið um íbúðarhúsnæði, sumarhús eða atvinnuhúsnæði.
                                </p>
                            }
                        />

                        <div className='row'>
                            <div className="col-lg-7 col-sm-12">
                                <div className="search__box">
                                    <SmallSearch
                                        search={searchInput}
                                        setSearch={setSearchInput}
                                        placeHolder="Leitaðu eftir heimilisfangi..."
                                        id="Search_Kaupsamningar"
                                    />
                                    <p className="info">Byggt á upplýsingum frá HMS frá 2006-{new Date().getFullYear()}</p>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="section--gray">
                        <div className="shell">
                            <DisplayBox>
                                <LmdTable
                                    tableSize='lg'
                                    apiData={kaupsamningar}
                                    columns={[
                                        {
                                            title: 'Fastanúmer',
                                            dataAccessor: ({ Fastnum }) => Fastnum,
                                            textAlign: 'left',
                                            simpleSortable: ({ Fastnum }) => Fastnum
                                        },
                                        {
                                            title: "Heimilisfang",
                                            dataAccessor: ({ Heimilisfang }) => (
                                                Heimilisfang
                                                // <Link
                                                //     url={`https://keldan.is/Property/PropertySearch?term=${Heimilisfang}`}
                                                //     linkSize='15'
                                                //     targetBlank
                                                // >
                                                //     {Heimilisfang}
                                                // </Link>
                                            ),
                                            textAlign: 'left',
                                            simpleSortable: ({ Heimilisfang }) => Heimilisfang
                                        },
                                        {
                                            title: 'Postnr',
                                            dataAccessor: ({ Postnr }) => Postnr,
                                            textAlign: 'left',
                                            simpleSortable: ({ Postnr }) => Postnr
                                        },
                                        {
                                            title: "Tegund",
                                            dataAccessor: ({ Tegund }) =>icelandifyString(Tegund),
                                            textAlign: 'left',
                                            simpleSortable: ({ Tegund }) => icelandifyString(Tegund)
                                        },
                                        {
                                            title: "Dags . Þingl.",
                                            dataAccessor: ({ Thinglystdags }) => format(new Date(Thinglystdags.replace(/ /g, "T")), "dd.MM.yyyy"),
                                            textAlign: 'right',
                                            simpleSortable: ({ Thinglystdags }) => Thinglystdags
                                        },
                                        {
                                            title: "Fasteignamat",
                                            dataAccessor: ({ Fasteignamat }) => numberWithDots(Fasteignamat.toFixed(0)) + " kr.",
                                            textAlign: 'right',
                                            simpleSortable: ({ Fasteignamat }) => Fasteignamat
                                        },
                                        {
                                            title: "Kaupverð",
                                            dataAccessor: ({ Kaupverd }) => numberWithDots(Kaupverd.toFixed(0)) + " kr.",
                                            textAlign: 'right',
                                            simpleSortable: ({ Kaupverd }) => Kaupverd
                                        },
                                        {
                                            title: "Stærð",
                                            dataAccessor: ({ Einflm, LodFlmein }) => {
                                                return <>{Einflm}{" "}{LodFlmein}</>
                                            },
                                            textAlign: 'right',
                                            simpleSortable: ({ Einflm }) => Einflm
                                        },
                                        {
                                            title: "Fermetraverð",
                                            dataAccessor: ({ Kaupverd, Einflm }) => {
                                                return <>{numberWithDots((Kaupverd / Einflm).toFixed(0))} kr.</>
                                            },
                                            textAlign: 'right',
                                            simpleSortable: ({ Kaupverd, Einflm }) => (Kaupverd / Einflm)
                                        },
                                        {
                                            title: (
                                                <Tooltip
                                                    text='Nothæfur samn.'
                                                    tooltip={
                                                        <span>
                                                            Samningur verður ónothæfur í vísitöluútreikning og<br/>
                                                            samanburð t.d. vegna þess að samningurinn er<br/>
                                                            - Milli skyldra<br/>
                                                            - Fjöldi fasteigna í samningi fleiri en 1<br/>
                                                            - Kvöð um notkun<br/>
                                                            - Fjármálastofnun selur<br/>
                                                            - Fjármálastofnun kaupir<br/>
                                                            - Greitt með lausafé<br/>
                                                            - Greitt með fasteign<br/>
                                                            - Selt að hluta<br/>
                                                            - Ófullnægjandi uppl. um staðgreiðsluverð<br/>
                                                            - Ástandi ábótavant<br/>
                                                        </span>
                                                    }
                                                />
                                            ),
                                            dataAccessor: ({ OnothaefurSamningur }) => OnothaefurSamningur ? "Nei" : "Já",
                                            textAlign: 'left',
                                            simpleSortable: ({ OnothaefurSamningur }) => (OnothaefurSamningur ? "Nei" : "Já")
                                        }, {
                                            title: "Verðmat",
                                            dataAccessor: ({ Fastnum }) => (
                                                <Button
                                                    size='sm'
                                                    buttonType='secondary'
                                                    anchorProps={{
                                                        target: '_blank',
                                                        href: `/Fasteignir/Leit?fastanr=${Fastnum}#Gangverd`
                                                    }}
                                                >
                                                    Verðvísir
                                                </Button>
                                            ),
                                            textAlign: 'left',
                                        }
                                    ]}
                                />
                            </DisplayBox>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    )
}

export default Kaupsamningar;